import * as React from 'react';
import { Progress } from 'reactstrap';
import { useSelector } from 'react-redux';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { getEntitiesDataItems } from '../../../store/entities/selectors';
import { ClientEntity } from '../../../store/entities/state';
import { ClientData } from '../../../models';
import { getErrorMessage } from '../../../ajax/errors';
import { createClientAsync, updateClientAsync } from '../../../ajax/requests';
import { AnalysisResult } from './useAnalyseCSV';
import { ColumnMapping } from './autoMap';
import { ImportOptions } from './ImportOptions';
import { partitionPhoneNumbers, parseRow } from './parseRow';

interface Props {
    result: AnalysisResult;
    mapping: ColumnMapping;
    onClose: () => void;
    options: ImportOptions;
}

type RowResult =
    | { status: 'created'; name: string }
    | { status: 'updated'; name: string }
    | { status: 'skipped'; name: string }
    | { status: 'error'; name: string; message: string; rowIndex: number; blocked?: boolean };

function extractErrorMessage(err) {
    return getErrorMessage(err) ?? (err instanceof Error ? err.message : undefined);
}

function appendNote(baseNote, extraNote) {
    const parts = [baseNote?.trim(), extraNote.trim()].filter(Boolean);
    return parts.length ? parts.join('\n') : undefined;
}

export function repairInvalidPhoneNumbers(data, invalidPhoneLabel) {
    const { validPhoneNumbers, invalidPhoneNumbers } = partitionPhoneNumbers(data.phoneNumbers ?? []);
    if (invalidPhoneNumbers.length === 0) return data;

    return {
        ...data,
        phoneNumbers: validPhoneNumbers.length > 0 ? validPhoneNumbers : undefined,
        notes: appendNote(data.notes, `${invalidPhoneLabel}: ${invalidPhoneNumbers.join(', ')}`),
    };
}

function buildClientData(rowIndex, result, mapping) {
    const row = result.allRows[rowIndex];
    return parseRow(row, result.headers, mapping);
}

function enrichClient(existing: ClientEntity, incoming: ClientData): ClientData {
    const merged: ClientData = { ...existing };
    for (const entry of Object.entries(incoming)) {
        const key = entry[0] as keyof ClientData;
        const value = entry[1];
        if (value === null || value === undefined) continue;
        const existingValue = existing[key as keyof ClientEntity];
        const isEmpty =
            existingValue === null ||
            existingValue === undefined ||
            existingValue === '' ||
            (Array.isArray(existingValue) && existingValue.length === 0);
        if (isEmpty) (merged as unknown as Record<string, unknown>)[key] = value;
    }
    return merged;
}

// Finds the first free "Name (N)" variant (N >= 2) not already taken by an
// existing or previously created client.
export function nextAvailableName(baseName: string, clientByName: Map<string, ClientEntity>): string {
    let n = 2;
    let candidate = `${baseName} (${n})`;
    while (clientByName.has(candidate.toLowerCase())) {
        n += 1;
        candidate = `${baseName} (${n})`;
    }
    return candidate;
}

export async function processRow(
    rowIndex: number,
    result: AnalysisResult,
    mapping: ColumnMapping,
    clientByName: Map<string, ClientEntity>,
    claimedNames: Set<string>,
    options: ImportOptions,
    invalidPhoneLabel?: string,
): Promise<RowResult> {
    const data = buildClientData(rowIndex, result, mapping);
    const preparedData = invalidPhoneLabel
        ? repairInvalidPhoneNumbers(data, invalidPhoneLabel)
        : data;
    const name = preparedData.name?.trim() ?? '';

    if (!name) return { status: 'skipped', name: '' };

    const key = name.toLowerCase();
    try {
        // A name already consumed by an earlier row in this same import is a
        // distinct person — create a separate "Name (2)", "Name (3)", … record
        // rather than merging the rows into one client.
        if (claimedNames.has(key)) {
            if (options.skipExistingOnly) return { status: 'skipped', name };
            const uniqueName = nextAvailableName(name, clientByName);
            const uniqueKey = uniqueName.toLowerCase();
            const created = await createClientAsync({ ...preparedData, name: uniqueName });
            clientByName.set(uniqueKey, { ...preparedData, name: uniqueName, id: created?.id ?? '' } as ClientEntity);
            claimedNames.add(uniqueKey);
            return { status: 'created', name: uniqueName };
        }

        const existing = clientByName.get(key);
        if (!existing) {
            const created = await createClientAsync(preparedData);
            clientByName.set(key, { ...preparedData, id: created?.id ?? '' } as ClientEntity);
            claimedNames.add(key);
            return { status: 'created', name };
        }
        if (options.skipExistingOnly) {
            claimedNames.add(key);
            return { status: 'skipped', name };
        }
        const enriched = enrichClient(existing, preparedData);
        await updateClientAsync(existing.id, enriched);
        claimedNames.add(key);
        return { status: 'updated', name };
    } catch (err) {
        return { status: 'error', name, message: extractErrorMessage(err) ?? '', rowIndex };
    }
}

const ImportStep: React.FC<Props> = ({ result, mapping, onClose, options }) => {
    const t = useTranslations();
    const existingClients = useSelector(getEntitiesDataItems<ClientEntity>('clients'));

    const [processed, setProcessed] = React.useState(0);
    const [results, setResults] = React.useState<RowResult[]>([]);
    const [isDone, setIsDone] = React.useState(false);
    const [wasCancelled, setWasCancelled] = React.useState(false);
    const [retryingRows, setRetryingRows] = React.useState<Set<number>>(new Set());

    const cancelRef = React.useRef(false);
    // Shared name→client map — populated at import start, updated as clients are created
    const clientByNameRef = React.useRef<Map<string, ClientEntity>>(new Map());
    // Names consumed by a row during this import — drives "Name (2)" disambiguation
    const claimedNamesRef = React.useRef<Set<string>>(new Set());

    const total = result.allRows.length;

    React.useEffect(() => {
        const map = new Map<string, ClientEntity>();
        existingClients.forEach(c => {
            if (c.name?.trim()) map.set(c.name.trim().toLowerCase(), c);
        });
        clientByNameRef.current = map;
        claimedNamesRef.current = new Set();

        let isMounted = true;
        const accumulated: RowResult[] = [];

        async function run() {
            for (let i = 0; i < result.allRows.length; i++) {
                if (cancelRef.current) {
                    if (isMounted) setWasCancelled(true);
                    break;
                }

                const rowResult = await processRow(i, result, mapping, clientByNameRef.current, claimedNamesRef.current, options);
                accumulated.push(rowResult);

                if (isMounted) {
                    setProcessed(i + 1);
                    setResults([...accumulated]);
                }
            }

            if (isMounted) setIsDone(true);
        }

        run();
        return () => { isMounted = false; };
    }, [result, mapping, options]);

    const retryRow = React.useCallback(async (rowIndex: number) => {
        setRetryingRows(prev => new Set(prev).add(rowIndex));
        try {
            const rowResult = await processRow(
                rowIndex,
                result,
                mapping,
                clientByNameRef.current,
                claimedNamesRef.current,
                options,
                t(TK.invalidPhoneNumber),
            );
            // On success the row leaves the error list; if it fails again we block
            // further retries and keep the reason on screen.
            const finalResult: RowResult = rowResult.status === 'error'
                ? { ...rowResult, blocked: true }
                : rowResult;
            setResults(prev => prev.map(r =>
                r.status === 'error' && r.rowIndex === rowIndex ? finalResult : r,
            ));
        } finally {
            setRetryingRows(prev => {
                const next = new Set(prev);
                next.delete(rowIndex);
                return next;
            });
        }
    }, [result, mapping, options, t]);

    const counts = React.useMemo(() => ({
        created: results.filter(r => r.status === 'created').length,
        updated: results.filter(r => r.status === 'updated').length,
        skipped: results.filter(r => r.status === 'skipped').length,
        errors: results.filter(r => r.status === 'error').length,
    }), [results]);

    const errorRows = results.filter(
        (r): r is Extract<RowResult, { status: 'error' }> => r.status === 'error'
    );

    const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
    const barColor = isDone && counts.errors > 0 ? 'warning' : 'success';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)' }}>
            {/* Progress bar */}
            <div>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.85rem',
                    color: 'var(--text-muted)',
                    marginBottom: 'var(--spacer-XS)',
                }}>
                    <span>{processed} / {total}</span>
                    <span>{percent}%</span>
                </div>
                <Progress value={percent} color={barColor} style={{ height: '8px' }} />
            </div>

            {/* Done / cancelled banner */}
            {isDone && (
                <div style={{
                    padding: 'var(--spacer-XS) var(--spacer-S)',
                    borderRadius: '6px',
                    background: wasCancelled ? 'var(--warning-bg)' : 'var(--success-bg)',
                    border: `var(--border-thin) solid ${wasCancelled ? 'var(--warning-border)' : 'var(--success-border)'}`,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: wasCancelled ? 'var(--warning-color)' : 'var(--success-color)',
                }}>
                    {wasCancelled ? t(TK.importClientsCancelled) : t(TK.importClientsDone)}
                </div>
            )}

            {/* Counters */}
            <div style={{
                display: 'flex',
                gap: 'var(--spacer-M)',
                flexWrap: 'wrap',
                fontSize: '0.9rem',
            }}>
                <span style={{ color: 'var(--success-color)' }}>
                    <strong>{counts.created}</strong> {t(TK.importClientsWillCreate)}
                </span>
                <span style={{ color: 'var(--appBlue)' }}>
                    <strong>{counts.updated}</strong> {t(TK.importClientsWillUpdate)}
                </span>
                <span style={{ color: 'var(--text-muted)' }}>
                    <strong>{counts.skipped}</strong> {t(TK.importClientsWillSkip)}
                </span>
                {counts.errors > 0 && (
                    <span style={{ color: 'var(--danger-color)' }}>
                        <strong>{counts.errors}</strong> {t(TK.importClientsErrors)}
                    </span>
                )}
            </div>

            {/* Error list */}
            {errorRows.length > 0 && (
                <div style={{
                    maxHeight: '220px',
                    overflowY: 'auto',
                    border: 'var(--border-thin) solid var(--danger-color)',
                    borderRadius: '6px',
                    padding: 'var(--spacer-S)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacer-XS)',
                }}>
                    {errorRows.map((r) => {
                        const isRetrying = retryingRows.has(r.rowIndex);
                        return (
                            <div
                                key={r.rowIndex}
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    gap: 'var(--spacer-S)',
                                    fontSize: '0.82rem',
                                    paddingBottom: 'var(--spacer-XS)',
                                    borderBottom: 'var(--border-thin) solid var(--appLigthGrey)',
                                }}
                            >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <strong>{r.name}</strong>:{' '}
                                    <span style={{ color: 'var(--danger-color)' }}>{r.message || t(TK.somethingWentWrong)}</span>
                                </div>
                                {!r.blocked && (
                                    <button
                                        onClick={() => retryRow(r.rowIndex)}
                                        disabled={isRetrying}
                                        style={{
                                            flexShrink: 0,
                                            padding: '2px var(--spacer-XS)',
                                            borderRadius: '4px',
                                            border: 'var(--border-thin) solid var(--appBlue)',
                                            background: 'white',
                                            color: isRetrying ? 'var(--text-muted)' : 'var(--appBlue)',
                                            cursor: isRetrying ? 'default' : 'pointer',
                                            fontSize: '0.78rem',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {isRetrying ? '...' : t(TK.retry)}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Cancel / Close */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacer-S)' }}>
                {!isDone && (
                    <button
                        onClick={() => { cancelRef.current = true; }}
                        style={{
                            padding: 'var(--spacer-XS) var(--spacer-S)',
                            borderRadius: '4px',
                            border: 'var(--border-thin) solid var(--appLigthGrey)',
                            background: 'white',
                            cursor: 'pointer',
                            color: 'var(--text-body)',
                            fontSize: '0.9rem',
                        }}
                    >
                        {t(TK.cancel)}
                    </button>
                )}
                {isDone && (
                    <button
                        onClick={onClose}
                        style={{
                            padding: 'var(--spacer-XS) var(--spacer-S)',
                            borderRadius: '4px',
                            border: 'none',
                            background: 'var(--appGreen)',
                            color: 'white',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                        }}
                    >
                        {t(TK.close)}
                    </button>
                )}
            </div>
        </div>
    );
};

export default ImportStep;
